import axios from 'axios';
import type { StayExtensionType } from './utils/stayExtensionTypes';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const getToken = () => localStorage.getItem('token') || '';
const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

// The type union and its two predicates live in utils/ so screens can import them without pulling
// an HTTP client along. Re-exported here because every caller already imports from this module.
export type { StayExtensionType } from './utils/stayExtensionTypes';
export { isArrival, isLuggage } from './utils/stayExtensionTypes';

export type StayExtensionMode = 'OFF' | 'AUTO' | 'REQUEST';
export type StayExtensionStatus =
  | 'REQUESTED' | 'APPROVED' | 'PAID' | 'DECLINED' | 'EXPIRED' | 'WITHDRAWN';

/** Which rule condition held a request back, when one did. */
export type StayExtensionBlocker = 'OFF_STAIR' | 'CLEANING_WINDOW' | 'CONTENDED' | 'NOTICE';

/**
 * Why a manager refused. A fixed set stored as a code rather than a sentence, so the guest reads
 * it in their own language and the wording can be improved without rewriting old records.
 */
export type StayExtensionDeclineReason =
  | 'CLEANING' | 'OTHER_BOOKING' | 'SHORT_NOTICE' | 'NO_SPACE' | 'UNSPECIFIED';

// ── Manager: settings ──────────────────────────────────────────────────────

export interface StayExtensionOffer {
  id: number;
  type: StayExtensionType;
  time: string;
  price: number;
}

export interface StayExtensionSettings {
  mode: StayExtensionMode;
  maxCleaningMinutes: number;
  autoApproveNoticeDays: number;
  standardCheckinTime: string;
  standardCheckoutTime: string;
  earlyCheckinOffers: StayExtensionOffer[];
  lateCheckoutOffers: StayExtensionOffer[];
  /** Luggage has its own switch: a host who can never spare an hour may still take the bags. */
  luggageMode: StayExtensionMode;
  luggageDropoffOffers: StayExtensionOffer[];
  luggagePickupOffers: StayExtensionOffer[];
  /** Where the bags go. Only ever shown to a guest who has bought storage. */
  luggageInstructions: string | null;
  luggageInstructionsEn: string | null;
}

export const getStayExtensionSettings = (apartmentId: number) =>
  axios.get<StayExtensionSettings>(`${API_BASE}/apartments/${apartmentId}/stay-extensions`, auth())
    .then(r => r.data);

export const updateStayExtensionSettings = (
  apartmentId: number,
  body: {
    mode?: StayExtensionMode;
    maxCleaningMinutes?: number;
    autoApproveNoticeDays?: number;
    luggageMode?: StayExtensionMode;
    // Undefined leaves the text alone; an empty string clears it.
    luggageInstructions?: string;
    luggageInstructionsEn?: string;
  },
) =>
  axios.put<StayExtensionSettings>(`${API_BASE}/apartments/${apartmentId}/stay-extensions`, body, auth())
    .then(r => r.data);

export const addStayExtensionOffer = (
  apartmentId: number,
  body: { type: StayExtensionType; time: string; price: number },
) =>
  axios.post<StayExtensionOffer>(`${API_BASE}/apartments/${apartmentId}/stay-extensions/offers`, body, auth())
    .then(r => r.data);

export const deleteStayExtensionOffer = (apartmentId: number, offerId: number) =>
  axios.delete(`${API_BASE}/apartments/${apartmentId}/stay-extensions/offers/${offerId}`, auth());

// ── Manager: the pending queue ─────────────────────────────────────────────

/**
 * One request as the manager sees it. The fields after `blockedBy` are the rule's own findings,
 * sent so the approval card can say which condition held it back rather than making the manager
 * work it out from the dates.
 */
export interface StayExtensionManagerRequest {
  id: number;
  reservationId: number;
  apartmentName: string;
  guestName: string;
  checkInDate: string;
  checkOutDate: string;
  type: StayExtensionType;
  requestedTime: string;
  approvedTime: string | null;
  standardTime: string;
  guestNote: string | null;
  status: StayExtensionStatus;
  approvedBy: 'MANAGER' | 'AUTO' | null;
  price: number | null;
  createdAt: string;
  quoteExpiresAt: string | null;
  paymentUrl: string | null;
  blockedBy: StayExtensionBlocker | null;
  minutesForCleaning: number | null;
  requiredCleaningMinutes: number | null;
  neighbourPending: boolean | null;
  neighbourHolds: boolean | null;
  noticeDays: number | null;
  requiredNoticeDays: number | null;
  cleaningAlreadyDone: boolean | null;
  declineReason: StayExtensionDeclineReason | null;
  /** Every hour this apartment prices at this end of the stay, for writing a counter-offer. */
  counterOptions: { time: string; price: number }[];
  /** False when this apartment has no chargeable Stripe account — approving promises an unpayable hour. */
  paymentsReady: boolean;
}

export const getPendingStayExtensions = () =>
  axios.get<StayExtensionManagerRequest[]>(`${API_BASE}/stay-extensions/pending`, auth())
    .then(r => r.data);

export const getPendingStayExtensionCount = () =>
  axios.get<{ count: number }>(`${API_BASE}/stay-extensions/pending/count`, auth())
    .then(r => r.data.count);

export const approveStayExtension = (
  requestId: number,
  body?: { approvedTime?: string; price?: number },
) =>
  axios.post<StayExtensionManagerRequest>(
    `${API_BASE}/stay-extensions/${requestId}/approve`, body ?? {}, auth())
    .then(r => r.data);

/**
 * The reason is required here even though the API defaults it — the guest is shown it.
 *
 * @param closeAllTimes closes this end of the stay rather than just the hour asked for. Refusals
 *   travel outward, not inward: declining 13:00 says nothing about 12:00, so this is never
 *   inferred. Reversible from the reservation page.
 */
export const declineStayExtension = (
  requestId: number,
  reason: StayExtensionDeclineReason,
  closeAllTimes: boolean,
) =>
  axios.post<StayExtensionManagerRequest>(
    `${API_BASE}/stay-extensions/${requestId}/decline`, { reason, closeAllTimes }, auth())
    .then(r => r.data);

// ── Reservation detail ─────────────────────────────────────────────────────

/** One end of a stay. Check-in and check-out differ only in which way their hours run. */
export interface StayExtensionEnd {
  type: StayExtensionType;
  time: string;                 // what applies now — granted if bought, else standard
  granted: boolean;
  pricePaid: number | null;
  paidAt: string | null;
  closed: boolean;              // no further requests accepted at this end
  closedReason: StayExtensionDeclineReason | null;
  closedAt: string | null;
  /** Who the ball is with. AWAITING_MANAGER is your move; AWAITING_PAYMENT is the guest's. */
  waitingOn: 'AWAITING_MANAGER' | 'AWAITING_PAYMENT' | null;
  waitingTime: string | null;
  waitingPrice: number | null;
  waitingSince: string | null;
  waitingUntil: string | null;
}

export interface StayExtensionReservationSummary {
  enabled: boolean;
  checkin: StayExtensionEnd;
  checkout: StayExtensionEnd;
  /** Both null when luggage is not sold at this apartment. */
  luggageDropoff: StayExtensionEnd | null;
  luggagePickup: StayExtensionEnd | null;
}

export const getStayExtensionSummary = (reservationId: number) =>
  axios.get<StayExtensionReservationSummary>(
    `${API_BASE}/reservations/${reservationId}/stay-extensions`, auth())
    .then(r => r.data);

/** Reopens requests at a closed end. Never undoes a paid grant — that is a refund. */
export const reopenStayExtensions = (reservationId: number, type?: StayExtensionType) =>
  axios.post<StayExtensionReservationSummary>(
    `${API_BASE}/reservations/${reservationId}/stay-extensions/reopen`, { type }, auth())
    .then(r => r.data);

// ── Guest: the check-in guide section ──────────────────────────────────────

export interface StayExtensionOption {
  time: string;
  price: number;
  /** true = paying settles it; false = the host has to agree first. */
  instant: boolean;
}

export interface StayExtensionBlock {
  type: StayExtensionType;
  standardTime: string;
  grantedTime: string | null;
  options: StayExtensionOption[];
}

export interface StayExtensionGuestRequest {
  id: number;
  type: StayExtensionType;
  requestedTime: string;
  approvedTime: string | null;
  status: StayExtensionStatus;
  price: number | null;
  paymentUrl: string | null;
  quoteExpiresAt: string | null;
  declineReason: StayExtensionDeclineReason | null;
  /** When the guest may chase this again. Null means now; only meaningful while REQUESTED. */
  canRemindAt: string | null;
}

export interface StayExtensionGuideSection {
  enabled: boolean;
  currency: string;
  earlyCheckin: StayExtensionBlock | null;
  lateCheckout: StayExtensionBlock | null;
  luggageDropoff: StayExtensionBlock | null;
  luggagePickup: StayExtensionBlock | null;
  /**
   * Where to leave the bags. Both languages arrive together and null until storage has actually
   * been bought — there is nothing to deliver before that, and it is not a detail to hand to
   * everyone holding the form link.
   */
  luggageInstructions: string | null;
  luggageInstructionsEn: string | null;
  requests: StayExtensionGuestRequest[];
}

export const getStayExtensionSection = (formToken: string) =>
  axios.get<StayExtensionGuideSection>(`${API_BASE}/public/checkin/${formToken}/stay-extensions`)
    .then(r => r.data);

export const requestStayExtension = (
  formToken: string,
  body: { type: StayExtensionType; time: string; guestNote?: string },
) =>
  axios.post<StayExtensionGuestRequest>(
    `${API_BASE}/public/checkin/${formToken}/stay-extensions`, body)
    .then(r => r.data);

export const withdrawStayExtension = (formToken: string, requestId: number) =>
  axios.delete(`${API_BASE}/public/checkin/${formToken}/stay-extensions/${requestId}`);

/** Nudges the manager about a waiting request. Throttled server-side, not just by the button. */
export const remindStayExtensionHost = (formToken: string, requestId: number) =>
  axios.post(`${API_BASE}/public/checkin/${formToken}/stay-extensions/${requestId}/remind`, {});

// ── Guest: the standalone payment page ─────────────────────────────────────

export interface StayExtensionQuote {
  apartmentName: string;
  checkInDate: string;
  checkOutDate: string;
  type: StayExtensionType;
  standardTime: string;
  grantedTime: string;
  price: number;
  currency: string;
  status: StayExtensionStatus;
  quoteExpiresAt: string | null;
}

/**
 * Addressed by the quote's own token, not the reservation's — a forwarded payment link must not
 * carry the check-in guide, and therefore the door code, along with it.
 */
export const getStayExtensionQuote = (paymentToken: string) =>
  axios.get<StayExtensionQuote>(`${API_BASE}/public/stay-extension/${paymentToken}`)
    .then(r => r.data);

export const startStayExtensionCheckout = (paymentToken: string) =>
  axios.post<{ checkoutUrl: string }>(
    `${API_BASE}/public/stay-extension/${paymentToken}/checkout-session`, {})
    .then(r => r.data.checkoutUrl);
